import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { adminAPI } from '../utils/api';
import { toast } from 'sonner';
import { Users, FileText, Send, Crown, UserX, MoreVertical, Eye, ArrowUpCircle, ArrowDownCircle, Ban, CheckCircle, TrendingUp, CreditCard, Calendar, Mail, Shield, Trash2, RefreshCw, IndianRupee, Percent, BarChart3, Ticket } from 'lucide-react';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const formatINR = (amount) => {
  if (amount >= 100000) return `${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
  return amount.toLocaleString('en-IN');
};

export const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState({ razorpay_payments: [], local_transactions: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, user: null });
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers()
      ]);
      setStats(statsRes.data);
      const usersData = usersRes.data?.users || usersRes.data || [];
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      toast.error('Failed to load admin data');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const res = await adminAPI.getPayments();
      setPayments(res.data);
    } catch (error) {
      console.error('Failed to load payments', error);
    }
  };

  const handleSyncRazorpay = async () => {
    setSyncing(true);
    try {
      const res = await adminAPI.syncRazorpay();
      toast.success(`Synced ${res.data.synced || 0} payments from Razorpay`);
      await Promise.all([loadData(), loadPayments()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync Razorpay payments');
    } finally {
      setSyncing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData();
      toast.success('Data refreshed');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleViewUser = async (user) => {
    try {
      const response = await adminAPI.getUserDetail(user.id);
      setSelectedUser(response.data);
      setUserDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleChangePlan = async (user, newPlan) => {
    try {
      await adminAPI.updateUserPlan(user.id, newPlan);
      toast.success(`User plan changed to ${newPlan.toUpperCase()}`);
      loadData();
      setConfirmDialog({ open: false, action: null, user: null });
    } catch (error) {
      toast.error('Failed to update user plan');
    }
  };

  const handleToggleStatus = async (user, newStatus) => {
    try {
      await adminAPI.updateUserStatus(user.id, newStatus);
      toast.success(`User ${newStatus ? 'activated' : 'suspended'}`);
      loadData();
      setConfirmDialog({ open: false, action: null, user: null });
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      await adminAPI.toggleAdminStatus(user.id);
      toast.success(`Admin status ${user.is_admin ? 'revoked' : 'granted'}`);
      loadData();
      setConfirmDialog({ open: false, action: null, user: null });
    } catch (error) {
      toast.error('Failed to update admin status');
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      await adminAPI.deleteUser(user.id);
      toast.success('User and all associated data deleted');
      loadData();
      setConfirmDialog({ open: false, action: null, user: null });
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const openConfirmDialog = (action, user) => {
    setConfirmDialog({ open: true, action, user });
  };

  const getPlanBadge = (plan) => {
    if (plan === 'pro') return <Badge className="bg-gradient-to-r from-slate-700 to-slate-900 text-white"><Crown className="w-3 h-3 mr-1" />Pro</Badge>;
    if (plan === 'growth' || plan === 'silver') return <Badge className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white"><Crown className="w-3 h-3 mr-1" />Growth</Badge>;
    return <Badge variant="secondary">Free</Badge>;
  };

  const getStatusBadge = (user) => {
    const isActive = user.subscription?.is_active !== false;
    if (isActive) return <Badge variant="outline" className="text-green-600 border-green-300">Active</Badge>;
    return <Badge variant="outline" className="text-red-600 border-red-300">Suspended</Badge>;
  };

  // Use REAL backend metrics from Razorpay
  const mrr = stats?.mrr || 0;
  const arr = stats?.arr || 0;
  const payingCustomers = stats?.paying_customers || 0;
  const conversionRate = stats?.conversion_rate || 0;
  const totalRevenue30d = stats?.total_revenue_30d || 0;
  const currency = stats?.currency || 'INR';
  const growthUsers = stats?.plan_distribution?.growth || 0;
  const proUsers = stats?.plan_distribution?.pro || 0;
  const freeUsers = stats?.plan_distribution?.free || 0;

  // Load payments when revenue tab is selected
  useEffect(() => {
    if (activeTab === 'revenue' && payments.razorpay_payments.length === 0) {
      loadPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-slate-900" data-testid="admin-title">Admin Panel</h1>
            <p className="text-slate-600 mt-1">Platform overview, revenue, and subscription management</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2"
              data-testid="refresh-data-btn"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </Button>
            <Link to="/admin/email-settings">
              <Button variant="outline" className="flex items-center space-x-2" data-testid="email-settings-link">
                <Mail className="h-4 w-4" />
                <span className="hidden sm:inline">Email Settings</span>
              </Button>
            </Link>
            <Link to="/admin/vouchers">
              <Button variant="outline" className="flex items-center space-x-2" data-testid="vouchers-link">
                <Ticket className="h-4 w-4" />
                <span className="hidden sm:inline">Vouchers</span>
              </Button>
            </Link>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
              <Card data-testid="admin-stat-users">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
                </CardContent>
              </Card>
              <Card data-testid="admin-stat-forms">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Forms</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.total_forms || 0}</div>
                </CardContent>
              </Card>
              <Card data-testid="admin-stat-submissions">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Submissions</CardTitle>
                  <Send className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.total_submissions || 0}</div>
                </CardContent>
              </Card>
              <Card data-testid="admin-stat-paid">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Paid Users</CardTitle>
                  <Crown className="h-4 w-4 text-indigo-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-indigo-600">{payingCustomers}</div>
                  <p className="text-xs text-muted-foreground">
                    {[
                      proUsers > 0 ? `${proUsers} Pro` : null,
                      growthUsers > 0 ? `${growthUsers} Growth` : null,
                    ].filter(Boolean).join(', ') || 'No paid users'}
                  </p>
                </CardContent>
              </Card>
              <Card data-testid="admin-stat-mrr">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">MRR</CardTitle>
                  <IndianRupee className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600" data-testid="mrr-value">{formatINR(mrr)}</div>
                  <p className="text-xs text-muted-foreground">Monthly revenue</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Revenue Summary */}
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <span>Revenue Summary</span>
                </CardTitle>
                <CardDescription>Real-time metrics from Razorpay</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-slate-600">Monthly Recurring (MRR)</p>
                    <p className="text-3xl font-bold text-green-600" data-testid="overview-mrr">{formatINR(mrr)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Annual Recurring (ARR)</p>
                    <p className="text-3xl font-bold text-slate-900" data-testid="overview-arr">{formatINR(arr)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Conversion Rate</p>
                    <p className="text-3xl font-bold text-indigo-600" data-testid="overview-conversion">{conversionRate}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Revenue (30 days)</p>
                    <p className="text-3xl font-bold text-slate-900" data-testid="overview-30d">{formatINR(totalRevenue30d)}</p>
                  </div>
                </div>
                <p className="text-xs text-green-700 mt-4 flex items-center">
                  <BarChart3 className="w-3 h-3 mr-1" />
                  Powered by Razorpay live data
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Revenue Analytics</h2>
              <Button
                onClick={handleSyncRazorpay}
                disabled={syncing}
                variant="outline"
                className="flex items-center space-x-2"
                data-testid="sync-razorpay-btn"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                <span>{syncing ? 'Syncing...' : 'Sync Razorpay'}</span>
              </Button>
            </div>

            {/* Revenue Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card data-testid="revenue-mrr">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">MRR</CardTitle>
                  <IndianRupee className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">{formatINR(mrr)}</div>
                  <p className="text-xs text-muted-foreground mt-1">Monthly Recurring Revenue</p>
                </CardContent>
              </Card>
              <Card data-testid="revenue-arr">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">ARR</CardTitle>
                  <Calendar className="h-4 w-4 text-blue-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">{formatINR(arr)}</div>
                  <p className="text-xs text-muted-foreground mt-1">Annual Recurring Revenue</p>
                </CardContent>
              </Card>
              <Card data-testid="revenue-paying">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Paying Customers</CardTitle>
                  <CreditCard className="h-4 w-4 text-indigo-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-indigo-600">{payingCustomers}</div>
                  <p className="text-xs text-muted-foreground mt-1">{growthUsers} Growth + {proUsers} Pro</p>
                </CardContent>
              </Card>
              <Card data-testid="revenue-conversion">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                  <Percent className="h-4 w-4 text-purple-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-600">{conversionRate}%</div>
                  <p className="text-xs text-muted-foreground mt-1">Free to paid conversion</p>
                </CardContent>
              </Card>
            </div>

            {/* Revenue Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue Breakdown</CardTitle>
                <CardDescription>Monthly revenue by plan type</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-slate-700 to-slate-900 rounded-lg flex items-center justify-center">
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">Pro Plan</p>
                        <p className="text-sm text-muted-foreground">1,499/month per user</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{formatINR(proUsers * 1499)}</p>
                      <p className="text-sm text-muted-foreground">{proUsers} subscribers</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-lg flex items-center justify-center">
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">Growth Plan</p>
                        <p className="text-sm text-muted-foreground">699/month per user</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{formatINR(growthUsers * 699)}</p>
                      <p className="text-sm text-muted-foreground">{growthUsers} subscribers</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-slate-300 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium">Free Plan</p>
                        <p className="text-sm text-muted-foreground">0/month</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">0</p>
                      <p className="text-sm text-muted-foreground">{freeUsers} users</p>
                    </div>
                  </div>
                </div>

                {/* Potential Revenue */}
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                    <p className="font-medium text-amber-800">Potential Revenue</p>
                  </div>
                  <p className="text-sm text-amber-700">
                    If all {freeUsers} free users converted to Growth, you'd earn an additional <span className="font-bold">{formatINR(freeUsers * 699)}/month</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Recent Payments */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Payments</CardTitle>
                <CardDescription>Synced from Razorpay and local transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {payments.razorpay_payments.length === 0 && payments.local_transactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground" data-testid="no-payments-msg">
                    <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p>No payment data yet. Click "Sync Razorpay" to fetch payment history.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Payment ID</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.razorpay_payments.map((p, idx) => (
                        <TableRow key={p.razorpay_payment_id || idx} data-testid={`payment-row-${idx}`}>
                          <TableCell className="font-mono text-xs">{p.razorpay_payment_id?.slice(-12) || '-'}</TableCell>
                          <TableCell className="text-sm">{p.email || '-'}</TableCell>
                          <TableCell className="font-medium">{formatINR(p.amount || 0)}</TableCell>
                          <TableCell>
                            <Badge variant={p.status === 'captured' ? 'default' : 'secondary'} className={p.status === 'captured' ? 'bg-green-600' : ''}>
                              {p.status || 'unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm capitalize">{p.method || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {p.created_at ? format(new Date(p.created_at), 'MMM dd, yyyy') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {payments.local_transactions.map((t, idx) => (
                        <TableRow key={t.id || idx} data-testid={`local-txn-row-${idx}`}>
                          <TableCell className="font-mono text-xs">{t.order_id?.slice(-12) || t.id?.slice(-12) || '-'}</TableCell>
                          <TableCell className="text-sm">{t.user_email || '-'}</TableCell>
                          <TableCell className="font-medium">{formatINR(t.amount_display || t.amount / 100 || 0)}</TableCell>
                          <TableCell>
                            <Badge variant={t.status === 'completed' ? 'default' : 'secondary'} className={t.status === 'completed' ? 'bg-green-600' : ''}>
                              {t.payment_status || t.status || 'unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{t.plan_name || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {t.created_at ? format(new Date(t.created_at), 'MMM dd, yyyy') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="grid md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Paid Users</CardTitle>
                  <Crown className="h-4 w-4 text-indigo-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-indigo-600">{payingCustomers}</div>
                  <p className="text-xs text-muted-foreground">
                    {[
                      proUsers > 0 ? `${proUsers} Pro` : null,
                      growthUsers > 0 ? `${growthUsers} Growth` : null,
                    ].filter(Boolean).join(', ') || 'No paid users'}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Free Users</CardTitle>
                  <Users className="h-4 w-4 text-slate-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{freeUsers}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Suspended</CardTitle>
                  <UserX className="h-4 w-4 text-red-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">{stats?.users_by_status?.suspended || 0}</div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Forms</TableHead>
                      <TableHead>Submissions</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(users || []).map((user, idx) => {
                      const isActive = user.subscription?.is_active !== false;
                      return (
                        <TableRow key={user.id} data-testid={`user-row-${idx}`}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{user.full_name}</div>
                              <div className="text-sm text-muted-foreground">{user.email}</div>
                            </div>
                          </TableCell>
                          <TableCell>{getPlanBadge(user.subscription?.plan)}</TableCell>
                          <TableCell>{getStatusBadge(user)}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <span className="font-medium">{user.usage?.forms_count || 0}</span>
                              <span className="text-muted-foreground"> / {user.limits?.max_forms >= 999999 ? '\u221e' : user.limits?.max_forms || 2}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <span className="font-medium">{user.usage?.submissions_this_month || 0}</span>
                              <span className="text-muted-foreground"> / {user.limits?.max_submissions_per_month || 50}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {user.created_at ? format(new Date(user.created_at), 'MMM dd, yyyy') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`user-actions-${idx}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewUser(user)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {user.subscription?.plan === 'free' || !user.subscription?.plan ? (
                                  <>
                                    <DropdownMenuItem onClick={() => openConfirmDialog('upgrade-growth', user)}>
                                      <ArrowUpCircle className="w-4 h-4 mr-2 text-indigo-500" />
                                      Upgrade to Growth
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openConfirmDialog('upgrade-pro', user)}>
                                      <ArrowUpCircle className="w-4 h-4 mr-2 text-slate-700" />
                                      Upgrade to Pro
                                    </DropdownMenuItem>
                                  </>
                                ) : user.subscription?.plan === 'growth' ? (
                                  <>
                                    <DropdownMenuItem onClick={() => openConfirmDialog('upgrade-pro', user)}>
                                      <ArrowUpCircle className="w-4 h-4 mr-2 text-slate-700" />
                                      Upgrade to Pro
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openConfirmDialog('downgrade', user)}>
                                      <ArrowDownCircle className="w-4 h-4 mr-2 text-orange-500" />
                                      Downgrade to Free
                                    </DropdownMenuItem>
                                  </>
                                ) : (
                                  <>
                                    <DropdownMenuItem onClick={() => openConfirmDialog('downgrade-growth', user)}>
                                      <ArrowDownCircle className="w-4 h-4 mr-2 text-indigo-500" />
                                      Downgrade to Growth
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openConfirmDialog('downgrade', user)}>
                                      <ArrowDownCircle className="w-4 h-4 mr-2 text-orange-500" />
                                      Downgrade to Free
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuSeparator />
                                {isActive ? (
                                  <DropdownMenuItem onClick={() => openConfirmDialog('suspend', user)} className="text-red-600">
                                    <Ban className="w-4 h-4 mr-2" />
                                    Suspend User
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => openConfirmDialog('activate', user)} className="text-green-600">
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Activate User
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => openConfirmDialog('toggle-admin', user)}
                                  className={user.is_admin ? "text-orange-600" : "text-blue-600"}
                                >
                                  <Shield className="w-4 h-4 mr-2" />
                                  {user.is_admin ? 'Revoke Admin' : 'Make Admin'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => openConfirmDialog('delete', user)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete User
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* User Detail Dialog */}
      <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>
              Detailed information about {selectedUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Name</label>
                  <p className="font-medium">{selectedUser.full_name}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Email</label>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Plan</label>
                  <div className="mt-1">{getPlanBadge(selectedUser.subscription?.plan)}</div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedUser)}</div>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-medium">Usage This Month</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Forms</span>
                      <span>{selectedUser.usage?.forms_count || 0} / {selectedUser.limits?.max_forms >= 999999 ? '\u221e' : selectedUser.limits?.max_forms}</span>
                    </div>
                    <Progress
                      value={selectedUser.limits?.max_forms >= 999999 ? 0 : ((selectedUser.usage?.forms_count || 0) / selectedUser.limits?.max_forms) * 100}
                      className="h-2"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Submissions</span>
                      <span>{selectedUser.usage?.submissions_this_month || 0} / {selectedUser.limits?.max_submissions_per_month}</span>
                    </div>
                    <Progress
                      value={((selectedUser.usage?.submissions_this_month || 0) / selectedUser.limits?.max_submissions_per_month) * 100}
                      className="h-2"
                    />
                  </div>
                </div>
              </div>
              {selectedUser.forms && selectedUser.forms.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">User's Forms ({selectedUser.forms.length})</h4>
                  <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                    {selectedUser.forms.map((form) => (
                      <div key={form.id} className="px-3 py-2 text-sm flex justify-between">
                        <span>{form.title}</span>
                        <span className="text-muted-foreground">{form.fields?.length || 0} fields</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Action Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.action === 'upgrade-growth' && 'Upgrade to Growth?'}
              {confirmDialog.action === 'upgrade-pro' && 'Upgrade to Pro?'}
              {confirmDialog.action === 'downgrade-growth' && 'Downgrade to Growth?'}
              {confirmDialog.action === 'downgrade' && 'Downgrade to Free?'}
              {confirmDialog.action === 'suspend' && 'Suspend User?'}
              {confirmDialog.action === 'activate' && 'Activate User?'}
              {confirmDialog.action === 'toggle-admin' && (confirmDialog.user?.is_admin ? 'Revoke Admin Status?' : 'Grant Admin Status?')}
              {confirmDialog.action === 'delete' && 'Delete User Permanently?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === 'upgrade-growth' && `This will upgrade ${confirmDialog.user?.full_name} to the Growth plan with increased limits.`}
              {confirmDialog.action === 'upgrade-pro' && `This will upgrade ${confirmDialog.user?.full_name} to the Pro plan with maximum limits.`}
              {confirmDialog.action === 'downgrade-growth' && `This will downgrade ${confirmDialog.user?.full_name} to the Growth plan.`}
              {confirmDialog.action === 'downgrade' && `This will downgrade ${confirmDialog.user?.full_name} to the Free plan. Their limits will be reduced.`}
              {confirmDialog.action === 'suspend' && `This will suspend ${confirmDialog.user?.full_name}'s account. They won't be able to access their forms.`}
              {confirmDialog.action === 'activate' && `This will reactivate ${confirmDialog.user?.full_name}'s account.`}
              {confirmDialog.action === 'toggle-admin' && (confirmDialog.user?.is_admin
                ? `This will revoke admin privileges from ${confirmDialog.user?.full_name}.`
                : `This will grant admin privileges to ${confirmDialog.user?.full_name}.`)}
              {confirmDialog.action === 'delete' && `This will PERMANENTLY delete ${confirmDialog.user?.full_name} and ALL their data. This action cannot be undone!`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDialog.action === 'upgrade-growth') handleChangePlan(confirmDialog.user, 'growth');
                if (confirmDialog.action === 'upgrade-pro') handleChangePlan(confirmDialog.user, 'pro');
                if (confirmDialog.action === 'downgrade-growth') handleChangePlan(confirmDialog.user, 'growth');
                if (confirmDialog.action === 'downgrade') handleChangePlan(confirmDialog.user, 'free');
                if (confirmDialog.action === 'suspend') handleToggleStatus(confirmDialog.user, false);
                if (confirmDialog.action === 'activate') handleToggleStatus(confirmDialog.user, true);
                if (confirmDialog.action === 'toggle-admin') handleToggleAdmin(confirmDialog.user);
                if (confirmDialog.action === 'delete') handleDeleteUser(confirmDialog.user);
              }}
              className={(confirmDialog.action === 'suspend' || confirmDialog.action === 'delete') ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};
